import React, { useEffect, useMemo, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { Divider, Popover, Spin } from 'antd';
import { CaretRightOutlined } from '@ant-design/icons';
import get from 'lodash/get';
// eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
import set from 'lodash/set';

import Button from 'components/Button';
import CardHeader from 'components/CardHeader';
import Loader from 'components/Loader';
import ActionMenu from 'modules/Media/components/ActionMenu';
import Article from 'modules/Media/components/Article';
import Container from 'modules/Media/components/ContainerArticles/Container';
import ExportPosts from 'modules/Media/components/ContainerArticles/exportPosts';
import ShareModal from 'modules/Media/components/ShareModal';
import TagsModal from 'modules/Media/components/TagsModal';

import {
  ArticleFilter,
  ArticleFilterAPI,
  formatArticleFilterAPI,
  useArticlesCacheUpdate
} from 'apiTyped/hooks/articles';
import {
  usePostSetMultipleFavoriteMutation,
  usePostSetMultipleReadMutation
} from 'apiTyped/hooks/post.mutations';
import { usePressHeaderCacheUpdate } from 'apiTyped/hooks/press.query';
import { useQueryClient } from '@tanstack/react-query';
import { components } from 'apiTyped/types';
import { useDisclose } from 'utils/hooks/useDisclosure';

// eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
import {
  translateArticlePreview,
  getLanguageClient,
  getLanguageArticle
} from 'utils/translation';
import Pagination from 'components/Pagination';

const isMobile = window.innerWidth < 845;

type ContainerArticlesProps = {
  articles: components['schemas']['Post'][];
  isLoading: boolean;
  count?: number;
  filterOption: ArticleFilter;
  page: number;
  totalPages: number;
  handlePrevious: () => void;
  handleNext: () => void;
};

export const ContainerArticles = ({
  articles,
  count,
  isLoading,
  filterOption,
  page,
  totalPages,
  handlePrevious,
  handleNext
}: ContainerArticlesProps) => {
  // create articlesTranslate
  const [loadingTranslate, setLoadingTranslate] = useState<boolean>(true);
  const [translateArticles, setTranslateArticles] = useState<any[]>([]);

  // @TODO use cache reactQueries for keep translate
  // const getTranslateArticles = async () => {
  //   return Promise.all(
  //     articles.map(async (article) => {
  //       const translation =
  //         getLanguageClient() !== getLanguageArticle(article)
  //           ? await translateArticlePreview(
  //               article.id,
  //               article.content.title,
  //               article.content.body
  //             )
  //           : false;
  //
  //       if (translation) {
  //         set(article, 'content.title', translation.title);
  //         set(article, 'content.body', translation.body);
  //       }
  //       return article;
  //     })
  //   );
  // };

  useEffect(() => {
    const prepare = async () => {
      // const listArticles = await getTranslateArticles();
      const listArticles = [...articles];
      setTranslateArticles(listArticles);
    };

    setLoadingTranslate(true);
    prepare().then(() => {
      setLoadingTranslate(false);
    });
  }, [articles]);

  const actionsMenu = useDisclose();
  const [selectedArticlesIds, setSelectedArticlesIds] = useState<number[]>([]);

  const [showTagModal, setShowTagModal] = useState(false);
  const [toTagArticleIds, setToTagArticleIds] = useState<number[]>([]);

  const shareArticleModalDisclosure = useDisclose();
  const [shareArticleId, setShareArticleId] = useState<number | null>(null);
  const shareArticle = useMemo(() => {
    return articles.find((article) => article.id === shareArticleId);
  }, [shareArticleId]);

  const handleTagArticle = (id: number) => {
    setShowTagModal(true);
    setToTagArticleIds([id]);
  };

  const queryClient = useQueryClient();
  const updateHeaderCache = usePressHeaderCacheUpdate();

  const filters: ArticleFilterAPI = formatArticleFilterAPI(filterOption);
  const updateArticleCache = useArticlesCacheUpdate(filters);

  const { mutate: setMultipleFavorites } =
    usePostSetMultipleFavoriteMutation(selectedArticlesIds);

  const handleMultipleFavorites = () => {
    setMultipleFavorites(selectedArticlesIds.map((id) => ({ id })));

    updateArticleCache((oldArticleResponse) => {
      if (!oldArticleResponse) return oldArticleResponse;

      const newArticles = oldArticleResponse.results.map((article) => {
        if (selectedArticlesIds.includes(article.id)) {
          return { ...article, is_favorite: true };
        }
        return article;
      });

      return {
        ...oldArticleResponse,
        results: newArticles
      };
    });
  };

  const { mutate: setMultipleIsRead } =
    usePostSetMultipleReadMutation(selectedArticlesIds);

  const handleMultipleRead = () => {
    setMultipleIsRead(selectedArticlesIds.map((id) => ({ id })));

    updateArticleCache((oldArticleResponse) => {
      if (!oldArticleResponse) return oldArticleResponse;

      const newArticles = oldArticleResponse.results.map((article) => {
        if (selectedArticlesIds.includes(article.id)) {
          return { ...article, is_read: true };
        }
        return article;
      });

      return {
        ...oldArticleResponse,
        results: newArticles
      };
    });
  };

  const handleSelectAll = (allSelected: boolean) => {
    setSelectedArticlesIds(
      allSelected
        ? selectedArticlesIds.filter(
            (id) => !articles.map((item) => item.id).includes(id)
          )
        : [
            ...new Set([
              ...selectedArticlesIds,
              ...articles.map((item) => item.id)
            ])
          ]
    );
  };

  const exportOptions = {
    tags: filterOption.tags,
    countries: filterOption.country,
    categories: filterOption.category?.value,
    is_read: filterOption.isRead,
    to_date: filterOption.toDate?.format('YYYY-MM-DD'),
    from_date: filterOption.fromDate?.format('YYYY-MM-DD')
  };

  const allSelected = articles.every((article) =>
    selectedArticlesIds.includes(article.id)
  );

  const showUnselectAll =
    selectedArticlesIds.filter((id) => !articles.map((a) => a.id).includes(id))
      .length > 0;

  const handleClickTag = () => {
    setToTagArticleIds(selectedArticlesIds);
    setShowTagModal(true);
    actionsMenu.onClose();
  };

  const handleClickFavorite = () => {
    handleMultipleFavorites();
    actionsMenu.onClose();
  };

  const handleClickRead = () => {
    handleMultipleRead();
    actionsMenu.onClose();
  };

  const handleSelect = (id: number) => {
    setSelectedArticlesIds(
      selectedArticlesIds.includes(id)
        ? selectedArticlesIds.filter((item) => item !== id)
        : [...selectedArticlesIds, id]
    );
  };

  const handleSingleFavorite = (id: number) => {
    updateArticleCache((oldArticleResponse) => {
      if (!oldArticleResponse) return oldArticleResponse;
      const newArticles = oldArticleResponse.results.map((article) => {
        if (article.id === id) {
          return { ...article, is_favorite: !article.is_favorite };
        }
        return article;
      });
      return {
        ...oldArticleResponse,
        results: newArticles
      };
    });
  };

  const handleSingleRead = (id: number) => {
    updateArticleCache((oldArticleResponse) => {
      if (!oldArticleResponse) return oldArticleResponse;
      const newArticles = oldArticleResponse.results.map((article) => {
        if (article.id === id) {
          return { ...article, is_read: true };
        }
        return article;
      });
      return {
        ...oldArticleResponse,
        results: newArticles
      };
    });
  };

  const handleTagSubmit = (
    selectedArticlesIds: number[],
    tagsToAdd: ({ id: number; name: string } | undefined)[],
    tagsToRemove: ({ id: number; name: string } | undefined)[]
  ) => {
    const newTags = tagsToAdd.filter(
      (item): item is { id: number; name: string } => item !== undefined
    );

    updateArticleCache((oldArticleResponse) => {
      if (!oldArticleResponse) return oldArticleResponse;

      const newArticles = oldArticleResponse.results.map((article) => {
        if (!selectedArticlesIds.includes(article.id)) return article;

        const filteredTags = article.tags.filter(
          (tag) => !tagsToRemove.find((t) => t?.id === tag.id)
        );
        const updatedTags = [...filteredTags, ...newTags];

        return { ...article, tags: updatedTags };
      });

      return {
        ...oldArticleResponse,
        results: newArticles
      };
    });

    updateHeaderCache((oldHeader: any) => {
      if (!oldHeader) return oldHeader;
      const oldTagsId = oldHeader.tags.map(
        (tag: components['schemas']['Entity']) => tag.id
      );
      const newHeader = { ...oldHeader };
      newTags.forEach((newTag: components['schemas']['Entity']) => {
        if (!oldTagsId.includes(newTag.id)) {
          newHeader.tags.push(newTag);
        }
      });
      return newHeader;
    });

    // Invalidate all search query caches so that filtering by the
    // newly added/removed tag triggers a fresh API fetch instead of
    // serving stale cached results.
    queryClient.invalidateQueries({
      queryKey: ['/communication/press/search'],
      refetchType: 'none'
    });
  };

  const handleShareClick = (id: number) => {
    shareArticleModalDisclosure.onOpen();
    const article = articles.find((article) => article.id === id);
    setShareArticleId(get(article, 'id', null));
  };

  useEffect(() => {
    if (!shareArticleModalDisclosure.isOpen) {
      setShareArticleId(null);
    }
  }, [shareArticleModalDisclosure]);

  return (
    <Container>
      <CardHeader
        title={
          <div className="card-header">
            <h1 className="ttTitle">{I18n.t('media.allArticles')}</h1>
          </div>
        }
        rightComponents={
          <ExportPosts
            postsIds={selectedArticlesIds}
            postsSearchFilter={exportOptions}
          />
        }
        fontSize="20px"
        iconColor="#e25c32"
        iconRight
        style={{ alignItems: 'center' }}
      />
      <Spin
        spinning={isLoading || loadingTranslate}
        style={{ maxHeight: '100%' }}
        indicator={
          <div>
            <Loader />
          </div>
        }
      >
        <div className="articlesContent">
          <div className="header">
            {!isMobile && (
              <>
                <div className="tooltip-container">
                  <Popover
                    content={
                      <ActionMenu
                        onClickShare={null}
                        originalContent={null}
                        onClickDlPdf={null}
                        isFavorite={null}
                        onClickFavorite={handleClickFavorite}
                        onClickRead={handleClickRead}
                        onClickTag={handleClickTag}
                        onClickTranslate={null}
                      />
                    }
                    trigger="click"
                    open={actionsMenu.isOpen}
                    onOpenChange={() => actionsMenu.set(!actionsMenu.isOpen)}
                  >
                    <Button
                      icon={null}
                      disabled={selectedArticlesIds.length === 0}
                      iconColor="white"
                      textTransform="none"
                      className={
                        'header-link actions-button ' +
                        (selectedArticlesIds.length === 0 ? 'disabled' : '')
                      }
                      label={
                        <div>
                          <p className="action-button-label">
                            {I18n.t(
                              'media.selectionArticles.actionButtonLabel'
                            ).toUpperCase()}
                          </p>
                          <CaretRightOutlined className="action-button-icon" />
                        </div>
                      }
                      fontFamily="Oswald"
                      onClick={() => {}}
                    />
                  </Popover>
                </div>
                <p className="compactDisplay offsetSmall">
                  <span>
                    {selectedArticlesIds && selectedArticlesIds.length > 0
                      ? selectedArticlesIds.length
                      : 0}{' '}
                    {I18n.t('media.selectionArticles.selected')} {count}{' '}
                    {I18n.t('media.selectionArticles.articles')}
                  </span>
                  <span
                    className="selectAll"
                    onClick={() => handleSelectAll(allSelected)}
                  >
                    {allSelected
                      ? I18n.t('media.selectionArticles.unselectShown')
                      : I18n.t('media.selectionArticles.selectShown')}
                  </span>
                  {showUnselectAll && (
                    <span
                      className="selectAll"
                      onClick={() => setSelectedArticlesIds([])}
                    >
                      {I18n.t('media.selectionArticles.selectedAll')}
                    </span>
                  )}
                </p>
              </>
            )}
          </div>
          <div className="boxArticles">
            {translateArticles.map((article) => (
              <>
                <Article
                  key={article.id}
                  article={article}
                  isSelected={selectedArticlesIds.includes(article.id)}
                  onAddTagClick={handleTagArticle}
                  onCheckBoxClick={handleSelect}
                  onFavoriteClick={handleSingleFavorite}
                  onReadClick={handleSingleRead}
                  onShareClick={handleShareClick}
                  handleTagSubmit={handleTagSubmit}
                />
                {isMobile && (
                  <Divider style={{ margin: 0, borderBlockWidth: 2 }} />
                )}
              </>
            ))}
          </div>
          {!isLoading && (
            <>
              {shareArticle && (
                <ShareModal
                  article={shareArticle}
                  open={shareArticleModalDisclosure.isOpen}
                  onClose={shareArticleModalDisclosure.onClose}
                />
              )}
              {toTagArticleIds.length > 0 && (
                <TagsModal
                  articleIds={toTagArticleIds}
                  articles={articles}
                  open={showTagModal}
                  onSubmit={handleTagSubmit}
                  onClose={() => {
                    setShowTagModal(false);
                    setToTagArticleIds([]);
                  }}
                />
              )}
            </>
          )}
        </div>
        <div className="navigation">
          <Pagination
            disableLeft={page === 1}
            disableRight={page === totalPages}
            onClickLeft={handlePrevious}
            onClickRight={handleNext}
          >
            <div className="navigation-pager">
              <div>{I18n.t('media.page')}</div>
              <div className="navigation-info">
                <span className="navigation-current">{page} </span>/{' '}
                {totalPages}
              </div>
            </div>
          </Pagination>
        </div>
      </Spin>
    </Container>
  );
};
